import { useCallback, useRef, useState } from 'react';
import { CdnFile } from '@services/core/cdn/file/file';
import { useImportContributionsMutation } from '@services/member/contribution/contribution.endpoints';
import { ImportResult } from '@services/member/contribution/contribution.types';
import { useFlowTs } from '@utils/useFlowTs';


export type ImportStatus = 'idle' | 'reading' | 'uploading' | 'importing' | 'complete' | 'error';

export const useSubscriptionsPageVM = () => {
    const [importStatus, setImportStatus] = useState<ImportStatus>('idle');
    const [totalRows, setTotalRows] = useState(0);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [importContributions] = useImportContributionsMutation();

    const handleImport = useCallback(async (cdnFile: CdnFile) => {
        setImportStatus('importing');
        try {
            const response = await importContributions({ fileId: cdnFile.id }).unwrap();
            setImportResult(response.data);
            setImportStatus('complete');
        } catch {
            setImportStatus('error');
            setErrorMessage("Une erreur est survenue lors de l'import.");
        }
    }, [importContributions]);

    const onCdnSuccess = useCallback((file: CdnFile) => {
        handleImport(file);
    }, [handleImport]);

    const onCdnError = useCallback(() => {
        setImportStatus('error');
        setErrorMessage("Erreur lors de l'upload du fichier.");
    }, []);

    const onCdnAdded = useCallback(() => {
        setImportStatus('uploading');
    }, []);

    const { flow } = useFlowTs({ onSuccess: onCdnSuccess, onError: onCdnError, onAdded: onCdnAdded });

    const handleFileSelected = useCallback(async (file: File) => {
        setImportStatus('reading');
        setImportResult(null);
        setErrorMessage(null);

        try {
            const XLSX = await import('xlsx');
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(ws);
            setTotalRows(data.length);
        } catch {
            setTotalRows(0);
        }

        if (flow) {
            flow.addFile(file);
        }
    }, [flow]);

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileSelected(file);
    }, [handleFileSelected]);

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    }, []);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFileSelected(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, [handleFileSelected]);

    const openFilePicker = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const reset = useCallback(() => {
        setImportStatus('idle');
        setTotalRows(0);
        setImportResult(null);
        setErrorMessage(null);
    }, []);

    return {
        importStatus,
        totalRows,
        importResult,
        errorMessage,
        fileInputRef,
        handleDrop,
        handleDragOver,
        handleInputChange,
        openFilePicker,
        reset,
    };
};
